/**
 * Role helpers — admin-section access vs full-admin powers.
 *
 * Mirrors `backend/src/middleware/role-gate.ts`. The OPS_MANAGER role
 * gets the admin shell (Home, Users, Purchases, Bookings, Financials,
 * Library, Feedback) but is blocked from destructive / escalation
 * actions (block / unblock / delete user, promote-to-admin, impersonate,
 * withdrawal approve/reject/process, withdrawal receipt upload,
 * platform settings, commission tiers, package edits, admin invites,
 * email templates, feedback delete).
 *
 * Usage:
 *   - `hasAdminAccess(role)` for admin-section gates (sidebar visibility,
 *     dashboard role selection, page-level admin redirects).
 *   - `isFullAdmin(role)` for destructive / escalation affordances
 *     (block button, impersonate menu item, withdrawal action buttons,
 *     Site Settings tabs, admin invite controls, etc.).
 *
 * Default-deny: a button or menu item that is unsafe for ops should call
 * `isFullAdmin`. Treat `hasAdminAccess` as an upper bound on visibility.
 */

export type AppRole = "STUDENT" | "TEACHER" | "ADMIN" | "OPS_MANAGER" | string;

/**
 * True for ADMIN and OPS_MANAGER. Use to gate admin-shell pages and
 * read-only admin affordances that ops is allowed to see.
 */
export function hasAdminAccess(role: AppRole | null | undefined): boolean {
  return role === "ADMIN" || role === "OPS_MANAGER";
}

/**
 * True for ADMIN only. Use for destructive / escalation actions ops
 * must NOT perform. If ops sees a button that ends up rejected by
 * `requireFullAdmin` on the backend, log a UX bug — the button should
 * have been hidden client-side too.
 */
export function isFullAdmin(role: AppRole | null | undefined): boolean {
  return role === "ADMIN";
}
